<template>
  <div :class="['mosaic-grid', `mosaic-grid--${mode}`]">
    <div
      v-for="(row, rowIndex) in rows"
      :key="rowIndex"
      class="mosaic-grid__row"
    >
      <div
        v-for="({ item, span }, itemIndex) in row"
        :key="itemIndex"
        :class="['mosaic-grid__card', `mosaic-grid__card--span-${String(span)}`]"
      >
        <slot
          :item="item"
          :span="span"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T">
  import { computed } from 'vue';

  import { useScreenSize } from '@/shared/utils/breakpoints';

  const props = defineProps<{ items: T[] }>();

  defineSlots<{
    default(props: { item: T; span: 1 | 2 }): unknown;
  }>();

  type Mode = 'desktop' | 'tablet' | 'mobile';
  type Span = 1 | 2;

  const patterns: Record<Mode, (count: number) => Span[][]> = {
    desktop: (count) => {
      const rows: Span[][] = [];
      const remainder = count % 3;
      let tripleCount = Math.floor(count / 3);
      let pairCount = 0;

      if (remainder === 1) {
        tripleCount = Math.max(0, tripleCount - 1);
        pairCount = 2;
      } else if (remainder === 2) {
        pairCount = 1;
      }

      const triples: Span[][] = [
        [1, 2, 1],
        [1, 1, 2],
        [2, 1, 1],
      ];

      for (let index = 0; index < tripleCount; index += 1) {
        rows.push(triples[index % triples.length] ?? [1, 2, 1]);
      }
      for (let index = 0; index < pairCount; index += 1) rows.push([2, 2]);

      return rows;
    },
    tablet: (count) => {
      const rows: Span[][] = [];
      const remainder = count % 3;
      const tripleCount = remainder === 0 ? count / 3 : remainder === 2 ? (count - 2) / 3 : (count - 4) / 3;
      const pairCount = remainder === 0 ? 0 : remainder === 2 ? 1 : 2;

      for (let index = 0; index < tripleCount; index += 1) rows.push([1, 1, 1]);
      for (let index = 0; index < pairCount; index += 1) rows.push(index % 2 === 0 ? [2, 1] : [1, 2]);

      return rows;
    },
    mobile: (count) => {
      const rows: Span[][] = [];
      let remaining = count;
      let useSolo = true;

      while (remaining > 0) {
        const forceDuo = !useSolo && remaining - 1 === 1;

        if (useSolo || forceDuo) {
          if (remaining === 1) {
            rows.push([2]);
            remaining -= 1;
          } else {
            rows.push([1, 1]);
            remaining -= 2;
          }
        } else {
          rows.push([2]);
          remaining -= 1;
        }

        useSolo = !useSolo;
      }

      return rows;
    },
  };

  const { isMobile, isTablet } = useScreenSize();
  const mode = computed<Mode>(() => (isMobile.value ? 'mobile' : isTablet.value ? 'tablet' : 'desktop'));

  const rows = computed(() => {
    const spans = patterns[mode.value](props.items.length);
    let itemIndex = 0;

    return spans.map((row) =>
      row.flatMap((span) => {
        const item = props.items[itemIndex];
        itemIndex += 1;

        return item === undefined ? [] : [{ item, span }];
      }),
    );
  });
</script>

<style scoped lang="scss">
  .mosaic-grid {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;

    &__row {
      display: grid;
      gap: 8px;
    }

    &--desktop &__row {
      grid-template-columns: repeat(4, 1fr);
    }

    &--tablet &__row {
      grid-template-columns: repeat(3, 1fr);
    }

    &--mobile &__row {
      grid-template-columns: repeat(2, 1fr);
    }

    &__card--span-1 {
      grid-column: span 1;
    }

    &__card--span-2 {
      grid-column: span 2;
    }
  }
</style>
